function ghOpenContact() {
  const contact = document.querySelector('[data-product-contact-drawer]');
  if (!contact) return false;
  if (!contact.open) contact.querySelector('summary')?.click();
  requestAnimationFrame(() => contact.querySelector('[data-product-contact-show-form]')?.focus({ preventScroll: true }));
  return true;
}
function ghServiceKeydown(event) {
  if (event.key !== ' ' || !event.target.closest('[data-gh-open-contact], [data-gh-localization]')) return;
  event.preventDefault();
  event.target.closest('a, button')?.click();
}
class GhNavigation extends HTMLElement {
  connectedCallback() {
    this.controller = new AbortController();
    const options = { signal: this.controller.signal };
    this.details = [...this.querySelectorAll('details')];
    this.reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
    const hover = matchMedia('(hover: hover) and (pointer: fine)');
    this.position = () => {
      const header = this.closest('hdt-sticky-header');
      if (!header) return;
      const top = `${header.getBoundingClientRect().bottom}px`;
      if (this.lastTop !== top) { document.documentElement.style.setProperty('--gh-nav-top', top); this.lastTop = top; }
    };
    this.visualDefaults = new Map([...this.querySelectorAll('[data-gh-visual]')].map(visual => [visual, {
      src: visual.querySelector('img')?.getAttribute('src'),
      srcset: visual.querySelector('img')?.getAttribute('srcset'),
      alt: visual.querySelector('img')?.alt,
      position: visual.querySelector('img')?.style.objectPosition,
      href: visual.getAttribute('href'),
      caption: visual.querySelector('[data-gh-caption]')?.textContent
    }]));
    this.details.forEach(details => {
      const summary = details.querySelector('summary');
      summary.setAttribute('aria-expanded', String(details.open));
      details.addEventListener('toggle', () => {
        summary.setAttribute('aria-expanded', String(details.open));
        if (!details.open) return;
        this.position();
        this.resetVisual(details.querySelector('[data-gh-area-panel]:not([hidden])'));
        this.details.forEach(other => { if (other !== details) other.open = false; });
        const panel = details.querySelector('[data-gh-panel]');
        if (!this.reducedMotion.matches) {
          panel.getAnimations().forEach(animation => animation.cancel());
          panel.animate([{ clipPath: 'inset(0 0 100% 0)' }, { clipPath: 'inset(0 0 0 0)' }], { duration: 280, easing: 'cubic-bezier(.16,1,.3,1)' });
        }
      }, options);
      details.addEventListener('pointerenter', () => {
        clearTimeout(this.closeTimer);
        if (hover.matches) this.openTimer = setTimeout(() => { details.open = true; }, 110);
      }, options);
      details.addEventListener('pointerleave', () => {
        clearTimeout(this.openTimer);
        if (hover.matches) this.closeTimer = setTimeout(() => { if (!details.contains(document.activeElement)) details.open = false; }, 190);
      }, options);
      details.addEventListener('focusout', event => {
        if (!details.contains(event.relatedTarget)) details.open = false;
      }, options);
      summary.addEventListener('keydown', event => {
        if (event.key === 'ArrowDown') {
          event.preventDefault();
          details.open = true;
          details.querySelector('[data-gh-area][data-gh-active="true"], [data-gh-panel] a')?.focus();
        }
      }, options);
    });
    this.areaLinks = [...this.querySelectorAll('[data-gh-area]')];
    this.areaLinks.forEach((tab, index) => {
      tab.addEventListener('pointerenter', () => { if (hover.matches) this.selectArea(tab); }, options);
      tab.addEventListener('focus', () => this.selectArea(tab), options);
      tab.addEventListener('keydown', event => {
        let next;
        if (event.key === 'ArrowDown') next = (index + 1) % this.areaLinks.length;
        if (event.key === 'ArrowUp') next = (index - 1 + this.areaLinks.length) % this.areaLinks.length;
        if (event.key === 'Home') next = 0;
        if (event.key === 'End') next = this.areaLinks.length - 1;
        if (next === undefined) return;
        event.preventDefault();
        this.selectArea(this.areaLinks[next]);
        this.areaLinks[next].focus();
      }, options);
    });
    this.addEventListener('pointerover', event => this.preview(event), options);
    this.addEventListener('focusin', event => this.preview(event), options);
    document.addEventListener('pointerdown', event => {
      if (!this.contains(event.target) || event.target.closest('[data-gh-dismiss]')) this.close();
    }, options);
    document.addEventListener('keydown', event => {
      if (event.key !== 'Escape') return;
      const open = this.details.find(details => details.open);
      if (open) { this.close(); open.querySelector('summary').focus(); }
    }, options);
    this.addEventListener('click', event => {
      const contact = event.target.closest('[data-gh-open-contact]');
      if (!contact || !document.querySelector('[data-product-contact-drawer]')) return;
      event.preventDefault();
      this.close();
      ghOpenContact();
    }, options);
    this.addEventListener('keydown', ghServiceKeydown, options);
    const schedulePosition = () => {
      if (this.positionFrame || !this.details.some(details => details.open)) return;
      this.positionFrame = requestAnimationFrame(() => { this.positionFrame = null; this.position(); });
    };
    window.addEventListener('resize', schedulePosition, options);
    window.addEventListener('scroll', schedulePosition, { ...options, passive: true });
    this.position();
  }
  close() {
    clearTimeout(this.openTimer);
    clearTimeout(this.closeTimer);
    this.details.forEach(details => { details.open = false; });
  }
  selectArea(tab) {
    const focusedPanel = this.querySelector('[data-gh-area-panel]:not([hidden])');
    const moveFocus = focusedPanel?.contains(document.activeElement);
    this.areaLinks.forEach(other => {
      const selected = other === tab;
      other.dataset.ghActive = String(selected);
    });
    this.querySelectorAll('[data-gh-area-panel]').forEach(panel => { panel.hidden = panel.dataset.ghAreaPanel !== tab.dataset.ghArea; });
    this.resetVisual(this.querySelector('[data-gh-area-panel]:not([hidden])'));
    if (moveFocus) tab.focus({ preventScroll: true });
  }
  resetVisual(panel) {
    const visual = panel?.querySelector('[data-gh-visual]');
    const original = this.visualDefaults.get(visual);
    const image = visual?.querySelector('img');
    if (!original || !image) return;
    visual.dataset.currentPreview = '';
    image.src = original.src;
    image.srcset = original.srcset || '';
    image.alt = original.alt || '';
    image.style.objectPosition = original.position || '50% 50%';
    visual.href = original.href;
    visual.querySelector('[data-gh-caption]').textContent = original.caption;
  }
  async preview(event) {
    const link = event.target.closest('[data-gh-preview]');
    if (!link || link.contains(event.relatedTarget)) return;
    const panel = link.closest('[data-gh-area-panel]');
    const visual = panel?.querySelector('[data-gh-visual]');
    const image = visual?.querySelector('img');
    if (!image || visual.dataset.currentPreview === link.dataset.ghPreview) return;
    visual.dataset.currentPreview = link.dataset.ghPreview;
    const source = link.dataset.ghPreview;
    const next = new Image();
    next.sizes = image.sizes;
    next.srcset = link.dataset.ghPreviewSrcset || '';
    next.src = source;
    try { await next.decode(); } catch { return; }
    if (visual.dataset.currentPreview !== source || !this.isConnected) return;
    image.srcset = link.dataset.ghPreviewSrcset;
    image.src = source;
    image.alt = link.dataset.ghPreviewAlt || '';
    image.style.objectPosition = link.dataset.ghPreviewPosition || '50% 50%';
    visual.href = link.dataset.ghPreviewHref;
    visual.querySelector('[data-gh-caption]').textContent = link.dataset.ghPreviewTitle;
    if (!this.reducedMotion.matches) {
      image.getAnimations().forEach(animation => animation.cancel());
      image.animate([{ opacity: .45, transform: 'scale(1.025)' }, { opacity: 1, transform: 'scale(1)' }], { duration: 240, easing: 'cubic-bezier(.16,1,.3,1)' });
    }
  }
  disconnectedCallback() {
    this.controller?.abort();
    clearTimeout(this.openTimer);
    clearTimeout(this.closeTimer);
    cancelAnimationFrame(this.positionFrame);
  }
}
class GhMobileNavigation extends HTMLElement {
  connectedCallback() {
    this.controller = new AbortController();
    const dialog = this.closest('dialog');
    dialog?.addEventListener('dialog:opening', () => {
      // Keep focus inside the modal without activating search or the keyboard.
      queueMicrotask(() => { if (dialog.open) dialog.focus({ preventScroll: true }); });
    }, { signal: this.controller.signal });
    this.addEventListener('keydown', ghServiceKeydown, { signal: this.controller.signal });
    const tabs = [...this.querySelectorAll('[data-gh-mobile-area]')];
    const selectArea = (tab, direction = 0) => {
      tabs.forEach(other => { const selected = tab === other; other.setAttribute('aria-selected', String(selected)); other.tabIndex = selected ? 0 : -1; });
      this.querySelectorAll('[data-gh-mobile-area-panel]').forEach(panel => { panel.hidden = panel.dataset.ghMobileAreaPanel !== tab.dataset.ghMobileArea; });
      if (direction && !matchMedia('(prefers-reduced-motion: reduce)').matches) {
        const panel = this.querySelector('[data-gh-mobile-area-panel]:not([hidden])');
        panel?.getAnimations().forEach(animation => animation.cancel());
        panel?.animate([{ opacity: .65, transform: `translateX(${direction * 12}px)` }, { opacity: 1, transform: 'translateX(0)' }], { duration: 180, easing: 'cubic-bezier(.16,1,.3,1)' });
      }
    };
    tabs.forEach((tab, index) => {
      tab.addEventListener('click', () => selectArea(tab), { signal: this.controller.signal });
      tab.addEventListener('keydown', event => {
        let next;
        if (event.key === 'ArrowRight') next = (index + 1) % tabs.length;
        if (event.key === 'ArrowLeft') next = (index - 1 + tabs.length) % tabs.length;
        if (event.key === 'Home') next = 0;
        if (event.key === 'End') next = tabs.length - 1;
        if (next === undefined) return;
        event.preventDefault(); selectArea(tabs[next]); tabs[next].focus();
      }, { signal: this.controller.signal });
    });
    const products = this.querySelector('[data-gh-mobile-page="products"]');
    let gesture;
    let suppressClickUntil = 0;
    const pointerOptions = { passive: true, signal: this.controller.signal };
    products?.addEventListener('pointerdown', event => {
      // Leave mouse input, browser edge gestures and multi-touch to the browser.
      if (!event.isPrimary) { gesture = null; return; }
      if (products.hidden || event.pointerType !== 'touch' || event.clientX < 24 || event.clientX > document.documentElement.clientWidth - 24) return;
      gesture = { id: event.pointerId, x: event.clientX, y: event.clientY, started: event.timeStamp };
    }, pointerOptions);
    products?.addEventListener('pointermove', event => {
      if (!gesture || gesture.id !== event.pointerId) return;
      const dx = Math.abs(event.clientX - gesture.x);
      const dy = Math.abs(event.clientY - gesture.y);
      if (dy > 12 && dy >= dx) gesture = null;
    }, pointerOptions);
    products?.addEventListener('pointercancel', () => { gesture = null; }, pointerOptions);
    products?.addEventListener('pointerup', event => {
      if (!gesture || gesture.id !== event.pointerId) return;
      const start = gesture;
      gesture = null;
      const dx = event.clientX - start.x;
      const dy = event.clientY - start.y;
      if (products.hidden || event.timeStamp - start.started > 1200 || Math.abs(dx) < 56 || Math.abs(dx) < Math.abs(dy) * 1.5) return;
      // A swipe starting on an image/link must never follow its synthetic click.
      suppressClickUntil = performance.now() + 400;
      const index = tabs.findIndex(tab => tab.getAttribute('aria-selected') === 'true');
      const direction = dx < 0 ? 1 : -1;
      const next = tabs[index + direction];
      if (next) selectArea(next, direction);
    }, pointerOptions);
    products?.addEventListener('click', event => {
      if (event.detail !== 0 && performance.now() < suppressClickUntil) {
        event.preventDefault();
        event.stopImmediatePropagation();
      }
    }, { capture: true, signal: this.controller.signal });
    this.addEventListener('click', async event => {
      const service = event.target.closest('[data-gh-open-contact], [data-gh-localization]');
      if (service) {
        const contact = document.querySelector('[data-product-contact-drawer]');
        const isContact = service.hasAttribute('data-gh-open-contact');
        if (isContact && !contact) return;
        if (!isContact && document.querySelector('[data-hdt-localization]')?.dataset.localizationControllerReady !== 'true') return;
        event.preventDefault();
        event.stopPropagation();
        if (this.openingService) return;
        this.openingService = true;
        try {
          // Await the native drawer lifecycle before opening another surface.
          await this.closest('hdt-drawer')?.close();
          if (!this.isConnected) return;
          if (isContact) {
            ghOpenContact();
          } else {
            document.dispatchEvent(new CustomEvent('hdt:localization:open', { bubbles: true, detail: { source: 'manual' } }));
          }
        } finally {
          this.openingService = false;
        }
        return;
      }
      const button = event.target.closest('[data-gh-go]');
      if (!button) return;
      const destination = [...this.querySelectorAll('[data-gh-mobile-page]')].find(page => page.dataset.ghMobilePage === button.dataset.ghGo);
      if (!destination) return;
      if (button.dataset.ghGo === 'products' && !button.hasAttribute('data-gh-back')) selectArea(tabs[0]);
      this.querySelectorAll('[data-gh-mobile-page]').forEach(page => { page.hidden = page !== destination; });
      this.closest('.hdt-dialog-drawer__inner').scrollTop = 0;
      this.closest('dialog').scrollTop = 0;
      if (!matchMedia('(prefers-reduced-motion: reduce)').matches) {
        destination.getAnimations().forEach(animation => animation.cancel());
        destination.animate([{ opacity: .65, transform: `translateX(${button.hasAttribute('data-gh-back') ? '-18px' : '18px'})` }, { opacity: 1, transform: 'translateX(0)' }], { duration: 220, easing: 'cubic-bezier(.16,1,.3,1)' });
      }
      const focus = destination.dataset.ghMobilePage === 'root' ? destination.querySelector('nav button, nav a') : destination.querySelector('button, input, a');
      focus?.focus({ preventScroll: true });
    }, { signal: this.controller.signal });
  }
  disconnectedCallback() { this.controller?.abort(); }
}
if (!customElements.get('gh-navigation')) customElements.define('gh-navigation', GhNavigation);
if (!customElements.get('gh-mobile-navigation')) customElements.define('gh-mobile-navigation', GhMobileNavigation);
